import {
  HEARTBEAT_INTERVAL_MS,
  serverFrameSchema,
  type ClientFrame,
  type ServerFrame,
} from '@avslack/shared';
import { getWsTicket } from '../lib/api';
import { pushEndpoint } from '../lib/push-device';
import { apiBase, isDesktop, isSameOriginApp } from '../lib/runtime';

export type ConnectionStatus = 'connecting' | 'online' | 'offline' | 'unauthorized';

type FrameListener = (frame: ServerFrame) => void;
type StatusListener = (status: ConnectionStatus, previous: ConnectionStatus) => void;

/** A szerver ezzel a koddal jelzi, hogy ujra be kell lepni - itt nincs ertelme ujraprobalni. */
const CLOSE_UNAUTHORIZED = 4401;

const MIN_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 30_000;

/**
 * WebSocket kliens.
 *
 * Feladatai a puszta kapcsolodason tul:
 *  - automatikus ujracsatlakozas exponencialis varakozassal es szorassal
 *    (szorás nelkul egy szerver-ujraindulas utan minden kliens egyszerre ternek vissza),
 *  - eleljel: ha ket periodus alatt nincs valasz, a kapcsolatot halottnak tekintjuk
 *    es ujraepitjuk - a TCP ezt nem mindig veszi eszre (alvo laptop, halozatvaltas),
 *  - azonnali ujraprobalkozas, ha a bongeszo halozatot vagy fokuszt jelez.
 *
 * A hivo az `onStatus`-bol tudja meg, mikor kell hezagot potolnia:
 * offline -> online atmenetnel minden nyitott csatornat ujra kell szinkronizalni.
 */
export class RealtimeClient {
  private socket: WebSocket | undefined;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private pingTimer: ReturnType<typeof setInterval> | undefined;
  private attempts = 0;
  private lastPongAt = 0;
  /** Az elozo eleljel-kor ideje - ebbol latszik, ha maga a mero allt. */
  private lastBeatAt = 0;
  private closedByUs = false;

  private readonly frameListeners = new Set<FrameListener>();
  private readonly statusListeners = new Set<StatusListener>();

  status: ConnectionStatus = 'offline';

  /**
   * A cimet FUGGVENY adja, nem fix ertek.
   *
   * A desktop appnak minden nyitas elott friss, egyszer hasznalhato jegyet
   * kell kernie (nincs sutije, es a bongeszo API nem enged fejlecet
   * socket-nyitaskor). Bongeszoben ez egy azonnali, szinkron ertek marad.
   */
  constructor(private readonly resolveUrl: () => string | Promise<string>) {}

  start(): void {
    this.closedByUs = false;
    this.open();

    window.addEventListener('online', this.handleWakeUp);
    document.addEventListener('visibilitychange', this.handleWakeUp);
  }

  stop(): void {
    this.closedByUs = true;
    window.removeEventListener('online', this.handleWakeUp);
    document.removeEventListener('visibilitychange', this.handleWakeUp);
    this.clearTimers();
    this.socket?.close(1000, 'client shutdown');
    this.socket = undefined;
    this.setStatus('offline');
  }

  onFrame(listener: FrameListener): () => void {
    this.frameListeners.add(listener);
    return () => this.frameListeners.delete(listener);
  }

  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  send(frame: ClientFrame): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(frame));
  }

  // ─── Belso mukodes ──────────────────────────────────────────────────────────

  private open(): void {
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) return;

    this.setStatus('connecting');

    void Promise.resolve(this.resolveUrl())
      .then((url) => {
        // A cim feloldasa kozben leallhattak, vagy nyilhatott masik kapcsolat.
        if (this.closedByUs) return;
        if (this.socket && this.socket.readyState <= WebSocket.OPEN) return;
        this.attach(new WebSocket(url));
      })
      .catch(() => {
        // Nem sikerult jegyet szerezni (pl. lejart session vagy halozati hiba).
        // Ugyanugy kezeljuk, mint egy megszakadt kapcsolatot: visszaszamlalas.
        this.setStatus('offline');
        if (!this.closedByUs) this.scheduleReconnect();
      });
  }

  private attach(socket: WebSocket): void {
    this.socket = socket;

    socket.addEventListener('message', (event) => {
      const parsed = serverFrameSchema.safeParse(JSON.parse(String(event.data)));
      if (!parsed.success) return; // ismeretlen keret: elore-kompatibilitas miatt eldobjuk

      const frame = parsed.data;
      if (frame.type === 'hello') {
        this.attempts = 0;
        this.lastPongAt = Date.now();
        this.startHeartbeat();
        // Rogton szolunk, lathato-e az ablak: enelkul a szerver az elso
        // eleljelig (15 mp) nem tudna, hogy kell-e push, vagy sem.
        this.sendPing(Date.now());
        this.setStatus('online');
      }
      if (frame.type === 'pong') {
        this.lastPongAt = Date.now();
        return;
      }

      for (const listener of this.frameListeners) listener(frame);
    });

    socket.addEventListener('close', (event) => {
      this.clearTimers();
      this.socket = undefined;

      if (event.code === CLOSE_UNAUTHORIZED) {
        this.setStatus('unauthorized');
        return;
      }
      this.setStatus('offline');
      if (!this.closedByUs) this.scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      // A 'close' mindig kovetkezik - ott kezeljuk az ujraprobalast.
    });
  }

  private startHeartbeat(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.lastBeatAt = Date.now();
    this.pingTimer = setInterval(() => this.beat(), HEARTBEAT_INTERVAL_MS);
  }

  /**
   * Egy eleljel-kor.
   *
   * ─── Miert nem eleg a "regen jott pong" feltetel? ───────────────────────────
   *
   * Az eleljel faliorat mer, de az ot hajto idozitot nem mi iranyitjuk: a
   * hatterbe kerult lapon a bongeszo percenkent egyre ritkitja, alvo gepen
   * pedig teljesen megallitja. Az elso ebredo kor ezert MINDIG hatalmas
   * kihagyast lat - fuggetlenul attol, hogy a kapcsolat kozben tokeletesen
   * elt-e. Emiatt az app minden elkattintas utan ujracsatlakozott.
   *
   * A kettot a KOROK tavolsaga valasztja szet. Ha maga a mero allt, akkor nem
   * a kapcsolatrol tudtunk meg valamit, hanem magunkrol: ilyenkor a meres
   * indul ujra, egy pinggel, aminek a valaszat mar a kovetkezo kor keri szamon.
   *
   * Egy valoban halott kapcsolat sem marad eszreveletlen, csak nehany korral
   * kesobb derul ki - es kozben ott a ket gyorsabb ut is: a bezarult socket
   * `close` esemenye, es a visszaerkezeskor kuldott azonnali kerdes. Cserebe
   * nem dobunk el egy elot minden alkalommal, amikor a felhasznalo elkattint.
   */
  private beat(): void {
    const now = Date.now();
    const sinceLastBeat = now - this.lastBeatAt;
    this.lastBeatAt = now;

    if (sinceLastBeat > HEARTBEAT_INTERVAL_MS * 2) {
      this.restartLivenessCheck(now);
      return;
    }

    // Ket kihagyott periodus utan a kapcsolatot halottnak tekintjuk. Ilyenkor
    // az ujraepites gyorsabb, mint varni, amig a TCP eszreveszi a szakadast.
    if (now - this.lastPongAt > HEARTBEAT_INTERVAL_MS * 2.5) {
      this.socket?.close(4000, 'heartbeat timeout');
      return;
    }

    this.sendPing(now);
  }

  /** A meres nullarol: a valasz hataridejet a kovetkezo kor nezi. */
  private restartLivenessCheck(now: number): void {
    this.lastPongAt = now;
    this.lastBeatAt = now;
    this.sendPing(now);
  }

  /**
   * Az eleljel viszi a lathatosagot is.
   *
   * A szerver ebbol tudja, kell-e push: egy nyitott, de elrejtett ablak nem
   * tud rendszer-ertesitest mutatni, tehat ugyanugy push kell neki, mintha
   * be lenne zarva. A push-feliratkozas azt mondja meg, MELYIK eszkozon:
   * a push eszkozonkent dont, lasd `lib/push-device.ts`.
   */
  private sendPing(now: number): void {
    this.send({
      type: 'ping',
      t: now,
      hidden: document.visibilityState === 'hidden',
      pushEndpoint: pushEndpoint() ?? undefined,
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;

    const base = Math.min(MAX_BACKOFF_MS, MIN_BACKOFF_MS * 2 ** this.attempts);
    const delay = base / 2 + Math.random() * (base / 2);
    this.attempts += 1;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      this.open();
    }, delay);
  }

  /** Halozat vissza vagy ful elore: nincs ertelme megvarni a visszaszamlalast. */
  private readonly handleWakeUp = (): void => {
    if (this.closedByUs) return;

    if (document.visibilityState === 'hidden') {
      /*
        Elrejteskor AZONNAL szolunk.

        Innentol a szerveren a push az egyetlen ut, ami rendszer-ertesitest
        tud mutatni. Ha a kovetkezo eleljelre varnank, addig az ide erkezo
        uzenetek jelzes nelkul maradnanak - es epp az elso masodpercekben a
        legvaloszinubb, hogy erkezik valami, hiszen az imenti kattintassal
        hagyta ott a beszelgetest.
      */
      this.sendPing(Date.now());
      return;
    }

    if (this.status === 'unauthorized') return;

    if (this.status === 'online') {
      /*
        Visszaerkezes: a kapcsolat lehet ZOMBI.

        Alvas vagy halozatvaltas utan a socket latszolag nyitva van, de mar
        nincs mogotte semmi. Egy azonnali kerdes elobb derit erre fenyt, mint
        a kovetkezo kor - es egyben megujitja az elorbe kerult ablak jelzeset.
      */
      this.restartLivenessCheck(Date.now());
      return;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    this.attempts = 0;
    this.open();
  };

  private clearTimers(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.pingTimer = undefined;
    this.reconnectTimer = undefined;
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return;
    const previous = this.status;
    this.status = status;
    for (const listener of this.statusListeners) listener(status, previous);
  }
}

/**
 * A WebSocket cime.
 *
 * Bongeszoben azonos origin, es a session suti hitelesit. Desktopban a
 * felulet helyi fajlokbol tolt be, ezert a szerver cimere megyunk, es
 * elotte kerunk egy rovid eletu, EGYSZER hasznalhato jegyet - a session
 * tokent nem tesszuk URL-be, mert a naplok megorzik.
 */
export async function buildWebSocketUrl(): Promise<string> {
  // Azonos origin (bongeszo vagy tavolrol toltott asztali app): a suti hitelesit.
  if (isSameOriginApp()) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/ws${clientParam('?')}`;
  }

  const base = apiBase().replace(/^http/, 'ws');
  const ticket = await getWsTicket();
  return `${base}/ws?ticket=${encodeURIComponent(ticket)}${clientParam('&')}`;
}

/**
 * Honnan kapcsolodunk - a hasznalati statisztika kliens szerinti bontasahoz.
 * A bongeszo socket-nyitaskor nem enged fejlecet, ezert URL-parameter.
 */
const clientParam = (separator: '?' | '&'): string =>
  isDesktop() ? `${separator}client=desktop` : '';
